import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { intl } from 'lang/intl'

import IconComponent from '../IconComponent/IconComponent'
import { WebinarHtmlDescriptionComponent as WebinarHtmlDescription } from '../WebinarHtmlDescriptionComponent/WebinarHtmlDescriptionComponent'

import StatusLabel from './StatusLabelComponent'
import SpeakerRow from './SpeakerRowComponent'

import './WebinarInfoPaneComponent.scss'

/**
 * DraftStatusIcon
 */
const DraftStatusIcon = () => (
  <IconComponent name="draft-icon" size={[12, 12]} color="f48630" />
)

/**
 * DraftStatusLabel
 */
const DraftStatusLabel = () => {
  return (
    <div className="webinar-info-pane-status-label">
      <StatusLabel
        backgroundColor="#fde2ce"
        description={intl.formatMessage({
          defaultMessage: 'In draft mode, you can test the room.',
        })}
        icon={<DraftStatusIcon />}
        title={intl.formatMessage({
          defaultMessage: 'Draft',
        })}
        titleColor="#f48630"
      />
    </div>
  )
}

/**
 * LiveStatusIcon
 */
const LiveStatusIcon = () => (
  <div className="webinar-info-pane-live-status-icon" />
)

/**
 * LiveStatusLabel
 */
const LiveStatusLabel = () => {
  return (
    <div className="webinar-info-pane-status-label">
      <StatusLabel
        backgroundColor="#eefbf3"
        description={intl.formatMessage({
          defaultMessage: 'You will be joining a live webinar.',
        })}
        icon={<LiveStatusIcon />}
        title={intl.formatMessage({
          defaultMessage: 'Live',
        })}
        titleColor="#29c95b"
      />
    </div>
  )
}

/**
 * SpeakerList
 */
const SpeakerList = ({ speakers, canDisableSpeakers }) =>
  speakers.map((speaker) => {
    return (
      <SpeakerRow
        key={speaker.live_speaker_id}
        name={speaker.name}
        title={speaker.title}
        link={speaker.link}
        linkedin={speaker.linkedin}
        facebook={speaker.facebook}
        twitter={speaker.twitter}
        company={speaker.company}
        avatarUrl={speaker.avatar_url}
        isHost={speaker.connected_role === 'producer'}
        disabled={canDisableSpeakers ? !speaker.display_as_connected_p : false}
      />
    )
  })

/**
 * WebinarInfoPaneComponent
 */
const WebinarInfoPaneComponent = ({
  title,
  description,
  status,
  speakers,
  canDisableSpeakers = false,
}) => (
  <div className="webinar-info-pane">
    {status === 'draft' && <DraftStatusLabel />}
    {status === 'live' && <LiveStatusLabel />}
    <h1 className="webinar-info-pane-title">{title}</h1>
    <div className="webinar-info-pane-html-description">
      <WebinarHtmlDescription html={description} />
    </div>
    {speakers.length > 0 && (
      <>
        <h2 className="webinar-info-pane-subtitle">
          <FormattedMessage defaultMessage="Speakers" />
        </h2>
        <SpeakerList
          speakers={speakers}
          canDisableSpeakers={canDisableSpeakers}
        />
      </>
    )}
  </div>
)

WebinarInfoPaneComponent.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['draft', 'upcoming', 'live']).isRequired,
  speakers: PropTypes.arrayOf(
    PropTypes.shape({
      avatar_url: PropTypes.string,
      company: PropTypes.string,
      connected_role: PropTypes.string,
      display_as_connected_p: PropTypes.bool,
      facebook: PropTypes.string,
      link: PropTypes.string,
      linkedin: PropTypes.string,
      live_speaker_id: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      name: PropTypes.string,
      title: PropTypes.string,
      twitter: PropTypes.string,
    })
  ),
  canDisableSpeakers: PropTypes.bool,
}

// oxlint-disable-next-line import/no-default-export
export default WebinarInfoPaneComponent
